import { h } from "harmaja"
import * as L from "lonna"
import {
    Board,
    Color,
    Item,
    Note,
    NoteShape,
    newContainer,
    newEmbed,
    newNote,
    newTable,
    newText,
} from "../../../../common/src/domain"
import * as G from "../../../../common/src/geometry"
import { Icon16 } from "../../components/Icon16"
import { BoardStore, Dispatch } from "../../store/board-store"
import { BoardCoordinateHelper } from "../board-coordinates"
import { BoardFocus } from "../board-focus"
import { toEmbedUrl } from "../embed-url"
import { DND_GHOST_HIDING_IMAGE } from "../item-drag"
import { localStorageAtom } from "../local-storage-atom"
import { Tool, ToolController } from "../tool-selection"
import { NOTE_COLORS, TRANSPARENT } from "../../../../common/src/colors"
import { onClickOutside } from "../../components/onClickOutside"

type StickySettings = { color: Color; shape: NoteShape }
type ToolbarPosition = { x?: number; y?: number; orientation: "vertical" | "horizontal" }

const STICKY_COLORS = NOTE_COLORS.filter((c) => c.color !== TRANSPARENT).map((c) => c.color)
const STICKY_SHAPES: { shape: NoteShape; w: number; h: number }[] = [
    { shape: "square", w: 5, h: 5 },
    { shape: "rect", w: 8, h: 4 },
    { shape: "round", w: 5, h: 5 },
]

// The dockable toolbar pill from the design handoff: drag handle | Select, Pan |
// Sticky (color+shape flyout), Text, Frame, Connect, Draw, Table, Embed | Undo, Redo
export const Toolbar = ({
    board,
    boardStore,
    containerElement,
    coordinateHelper,
    dispatch,
    focus,
    onAdd,
    toolController,
}: {
    board: L.Property<Board>
    boardStore: BoardStore
    containerElement: L.Atom<HTMLElement | null>
    coordinateHelper: BoardCoordinateHelper
    dispatch: Dispatch
    focus: L.Atom<BoardFocus>
    onAdd: (i: Item) => void
    toolController: ToolController
}) => {
    const tool = toolController.tool
    const sticky = localStorageAtom<StickySettings>("stickySettings", { color: STICKY_COLORS[0], shape: "square" })
    const flyoutOpen = L.atom(false)
    const rootEl = L.atom<HTMLElement | null>(null)
    onClickOutside(rootEl, () => flyoutOpen.set(false))

    const toolbarPosition = localStorageAtom<ToolbarPosition>("toolbarPosition2", { orientation: "horizontal" })

    // drag-to-dock (ported from the old toolbar)
    let cursorPosAtStart: G.Coordinates | null = null
    let elementStartPos: G.Rect | null = null
    const onDrag = (e: JSX.DragEvent) => {
        if (!cursorPosAtStart) return
        e.preventDefault()
        const cursorCurrentPos = coordinateHelper.currentPageCoordinates.get()
        const diff = G.subtract(cursorCurrentPos, cursorPosAtStart)
        const minY = 70
        const minX = 16
        const boardRect = containerElement.get()!.getBoundingClientRect()
        const boardViewCenter = boardRect.x + boardRect.width / 2
        const toolbarCenter = elementStartPos!.x + diff.x + elementStartPos!.width / 2
        const centerDiff = Math.abs(toolbarCenter - boardViewCenter)
        const newPos = {
            x: Math.max(elementStartPos!.x + diff.x, minX),
            y: Math.max(elementStartPos!.y + diff.y, minY),
        }
        if (centerDiff < 60 && newPos.y > window.innerHeight - 160) {
            toolbarPosition.set({ orientation: "horizontal" }) // default bottom-center dock
        } else {
            toolbarPosition.set({ ...newPos, orientation: newPos.x < 100 ? "vertical" : "horizontal" })
        }
    }
    const onDragStart = (e: JSX.DragEvent) => {
        cursorPosAtStart = { x: e.clientX, y: e.clientY }
        e.dataTransfer?.setDragImage(DND_GHOST_HIDING_IMAGE, 0, 0)
        elementStartPos = rootEl.get()!.getBoundingClientRect()
    }
    const onDragOver = (e: JSX.DragEvent) => {
        e.preventDefault()
        coordinateHelper.currentPageCoordinates.set({ x: e.clientX, y: e.clientY })
    }
    const toolbarStyle = L.view(toolbarPosition, (p) => ({
        top: p.y !== undefined ? p.y + "px" : undefined,
        left: p.x !== undefined ? p.x + "px" : undefined,
        transform: p.x !== undefined ? "none" : undefined,
    }))

    const startSticky = () => {
        const s = sticky.get()
        const dims = STICKY_SHAPES.find((x) => x.shape === s.shape) ?? STICKY_SHAPES[0]
        focus.set({
            status: "adding",
            element: <StickyPreview color={s.color} shape={s.shape} />,
            item: newNote("", s.color, 20, 20, dims.w, dims.h, s.shape),
        })
        tool.set("note")
    }
    const pickColor = (color: Color) => {
        sticky.modify((s) => ({ ...s, color }))
        startSticky()
    }
    const pickShape = (shape: NoteShape) => {
        sticky.modify((s) => ({ ...s, shape }))
        startSticky()
        flyoutOpen.set(false)
    }

    const startAdding = (t: Tool, createItem: () => Item) => () => {
        flyoutOpen.set(false)
        focus.set({ status: "adding", element: <span className="icon16" />, item: createItem() })
        tool.set(t)
    }
    const startEmbed = () => {
        flyoutOpen.set(false)
        const input = prompt("URL to embed (YouTube, Vimeo, Loom, Figma, Google Docs, or any https page)")
        if (!input) return
        const url = toEmbedUrl(input)
        if (!url) {
            alert("That doesn't look like a valid http(s) URL")
            return
        }
        focus.set({ status: "adding", element: <span className="icon16" />, item: newEmbed(url) })
        tool.set("embed")
    }
    const selectTool = (t: Tool) => () => {
        flyoutOpen.set(false)
        tool.set(t)
    }

    const toolClass = (t: Tool | null, extra = "") =>
        L.view(tool, (cur) => "tbtn" + (t !== null && cur === t ? " active" : "") + extra)

    const ToolButton = (p: { t: Tool | null; icon: string; tooltip: string; onClick: () => void; extra?: string }) => (
        <button className={toolClass(p.t, p.extra ?? "")} data-tooltip={p.tooltip} onClick={p.onClick}>
            <Icon16 name={p.icon} />
        </button>
    )

    return (
        <div
            className={L.view(toolbarPosition, (o) => "lousa-toolbar " + o.orientation)}
            style={toolbarStyle}
            ref={rootEl.set}
            onDragOver={onDragOver}
        >
            <span
                className="drag-handle"
                draggable={true}
                onDragStart={onDragStart}
                onDrag={onDrag}
                onDragEnd={() => (cursorPosAtStart = null)}
                title="Drag to move the toolbar"
            >
                {L.view(toolbarPosition, (p) => (
                    <Icon16 name={p.orientation === "vertical" ? "dragHandleH" : "dragHandleV"} />
                ))}
            </span>
            <ToolButton t="select" icon="toolSelect" tooltip="Select · V" onClick={selectTool("select")} />
            <ToolButton t="pan" icon="toolPan" tooltip="Pan · Space" onClick={selectTool("pan")} />
            <span className="divider" />
            <span className="sticky-wrap">
                <button
                    className={L.view(tool, (cur) => "tbtn sticky" + (cur === "note" ? " active" : ""))}
                    data-tooltip="Sticky · N"
                    onClick={() => {
                        startSticky()
                        flyoutOpen.modify((o) => !o)
                    }}
                >
                    <Icon16 name="toolSticky" />
                    <span className="sticky-swatch" style={L.view(sticky, (s) => ({ background: s.color }))} />
                </button>
                {L.view(flyoutOpen, (open) =>
                    open ? (
                        <div className="sticky-flyout">
                            <span className="micro-label">Sticky</span>
                            <div className="swatch-row">
                                {STICKY_COLORS.map((c) => (
                                    <span
                                        className={L.view(sticky, (s) =>
                                            s.color === c ? "swatch selected" : "swatch",
                                        )}
                                        style={{ background: c }}
                                        onClick={() => pickColor(c)}
                                    />
                                ))}
                            </div>
                            <span className="flyout-divider" />
                            <div className="shape-row">
                                {STICKY_SHAPES.map((s) => (
                                    <span
                                        className={L.view(sticky, (cur) =>
                                            cur.shape === s.shape
                                                ? "shape-opt " + s.shape + " selected"
                                                : "shape-opt " + s.shape,
                                        )}
                                        style={L.view(sticky, (cur) => ({ background: cur.color }))}
                                        onClick={() => pickShape(s.shape)}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null,
                )}
            </span>
            <ToolButton
                t="text"
                icon="toolText"
                tooltip="Text · T"
                onClick={startAdding("text", () => newText(board.get().crdt))}
            />
            <ToolButton
                t="container"
                icon="toolFrame"
                tooltip="Frame · A"
                onClick={startAdding("container", () => newContainer(board.get().crdt))}
            />
            <ToolButton t="connect" icon="toolConnect" tooltip="Connect · C" onClick={selectTool("connect")} />
            <ToolButton t="draw" icon="toolDraw" tooltip="Draw · P" onClick={selectTool("draw")} />
            <ToolButton t="table" icon="toolTable" tooltip="Table" onClick={startAdding("table", () => newTable())} />
            <ToolButton t="embed" icon="toolEmbed" tooltip="Embed" onClick={startEmbed} />
            <span className="divider" />
            <button
                className={L.view(boardStore.canUndo, (can) => "tbtn" + (can ? "" : " disabled"))}
                data-tooltip="Undo · ⌘Z"
                onClick={() => dispatch({ action: "ui.undo" })}
            >
                <Icon16 name="undo" />
            </button>
            <button
                className={L.view(boardStore.canRedo, (can) => "tbtn" + (can ? "" : " disabled"))}
                data-tooltip="Redo · ⇧⌘Z"
                onClick={() => dispatch({ action: "ui.redo" })}
            >
                <Icon16 name="redo" />
            </button>
        </div>
    )
}

const StickyPreview = ({ color, shape }: { color: Color; shape: NoteShape }) => (
    <span className={"sticky-preview " + shape} style={{ background: color }} />
)
